import React, { useState, useRef, useEffect } from 'react';
import { useRadixConnect } from '../../../radix_connect';
import { RADIX_API_GATEWAY_URL, DCKSLAP_USER_BADGE_RESOURCE_ADDRESS, REDDICKS_RESSOURCE_ADDRESS } from '../../../constants';
import { FaHandPaper, FaQuestionCircle, FaRocket } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import './Homepage.css';


const HomepageContent = () => {
  const { connected, selectedAccount } = useRadixConnect();
  const [userBalance, setUserBalance] = useState(0);
  const [userBadges, setUserBadges] = useState([]);
  const [hasRedicks, setHasRedicks] = useState(false);
  const navigate = useNavigate();

  const hasEnoughBalance = userBalance >= 15000;

  useEffect(() => {
    if (connected && selectedAccount) {
      const fetchAccountState = async () => {
        try {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/entity/details",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                addresses: [selectedAccount.address],
                aggregation_level: "Vault",
                opt_ins: {
                  non_fungible_include_nfids: true,
                },
              }),
            }
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const data = await response.json();
          const entity = data.items[0];
          const fungible = entity?.fungible_resources?.items.find(
            (item) => item.resource_address === REDDICKS_RESSOURCE_ADDRESS
          );
          const totalAmount = fungible?.vaults?.items?.reduce((sum, vault) => {
            return sum + parseFloat(vault.amount || "0");
          }, 0) ?? 0;
          setUserBalance(Math.floor(totalAmount));

          const nonFungible = entity?.non_fungible_resources?.items.find(
            (item) =>
              item.resource_address === DCKSLAP_USER_BADGE_RESOURCE_ADDRESS
          );

          if (
            nonFungible &&
            nonFungible.vaults &&
            nonFungible.vaults.items.length > 0
          ) {
            const badgeIds = nonFungible.vaults.items.flatMap(
              (vault) => vault.items
            );
            const extractedIds = badgeIds
              .map((idStr) => {
                const match = idStr.match(/#(\d+)#/);
                return match ? match[1] : null;
              })
              .filter((id) => id !== null);
            setUserBadges(extractedIds);
          } else {
            setUserBadges([]);
          }
        } catch (error) {
          console.error("Failed to fetch account state:", error);
          setUserBalance(0);
          setUserBadges([]);
        }
      };

      fetchAccountState();
    } else {
      setUserBalance(0);
      setUserBadges([]);
      setHasRedicks(false);
    }
  }, [connected, selectedAccount]);

  useEffect(() => {
    if (userBadges.length > 0) {
      const checkHasDicks = async () => {
        try {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/state/non-fungible/data",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                resource_address: DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
                non_fungible_ids: [`#${userBadges[0]}#`],
              }),
            }
          );
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          const data = await response.json();
          const fields =
            data.non_fungible_ids[0]?.data?.programmatic_json?.fields;
          if (!fields) {
            return;
          }
          const has_redicks = fields.find(
            (field) => field.field_name === "has_dicks"
          )?.value;
          setHasRedicks(has_redicks);
        } catch (error) {
          console.error("Failed to check DCKSLAP balance:", error);
        }
      };
      checkHasDicks();
    } else {
      setHasRedicks(false);
    }
  }, [userBadges]);

  const getCTAButton = () => {
    if (!connected) {
      return {
        text: 'Connect Wallet to Join the Fun',
        variant: 'default',
        action: 'connect'
      };
    } else if (!hasEnoughBalance && !hasRedicks) {
      return {
        text: 'You Need More $REDDICKS!',
        variant: 'outline',
        action: 'buy'
      };
    } else if (hasRedicks) {
      return {
        text: 'START DCKSLAPPING',
        variant: 'success',
        action: 'slap'
      };
    } else {
      return {
        text: 'Generate Your First Slap!',
        variant: 'success',
        action: 'slap'
      };
    }
  };

  const ctaButton = getCTAButton();
  const cardsRef = useRef(null);

  const handleCTAClick = () => {
    switch (ctaButton.action) {
      case 'connect':
        break;
      case 'buy':
        window.open('https://www.caviarnine.com/trade/', '_blank');
        break;
      case 'slap':
        navigate('/dckslap/slapgenerator');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen text-foreground px-4 md:px-8 py-8 md:py-12">

      {/* Hero Section */}
      <section className="relative max-w-3xl mx-auto text-center mb-12 md:mb-20">
        {/* Decorative glow behind title */}
        <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-48 rounded-full blur-3xl opacity-20 pointer-events-none" />

        <h1 className="hero-title relative text-4xl md:text-6xl font-bold tracking-widest mb-6 text-foreground">
          Welcome to DCKBase.
        </h1>
      </section>

      {/* Content Cards */}
      <section ref={cardsRef} className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">

          {/* What is DCKSLAP Card */}
          <div className="group relative">
            {/* Floating icon */}
            <div className="floating-icon-primary absolute -top-6 left-8 z-10 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
              <FaHandPaper className="text-2xl text-white" />
            </div>

            {/* shadcn Card */}
            <Card className="homepage-card-gradient pt-10 hover:translate-y-[-2px] transition-all duration-300">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-semibold text-primary mb-4">
                  What is DCKSLAP?
                </h3>

                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-8">
                  Hold $REDDICKS to mint DCKSLAPs.<br />
                  Give someone a DCKSlap, create memes, unlock GBOFs (Great balls of fire) and show a little Balls!
                </p>

                <div className="dckbase-cta-stack">
                  {ctaButton.action === 'connect' ? (
                    <div
                      className="
                        text-sm text-muted-foreground text-center
                        py-4 px-6 rounded-xl
                        border border-dashed border-primary/30
                        bg-primary/5
                      "
                    >
                      Connect your wallet now to start DCKSLAPPING
                    </div>
                  ) : (
                    <Button
                      variant={ctaButton.variant}
                      size="lg"
                      onClick={handleCTAClick}
                      className={`w-full font-semibold tracking-wide ${ctaButton.variant === 'outline' ? 'cta-outline-btn' : ''}`}
                    >
                      {ctaButton.text}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => navigate('/dckslap/howtoplay')}
                    className="w-full font-semibold tracking-wide dckbase-secondary-btn"
                  >
                    <FaQuestionCircle className="mr-2 h-4 w-4" />
                    How to Play
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Powered by REDDICKS Card */}
          <div className="group relative">
            {/* Floating icon */}
            <div className="floating-icon-secondary absolute -top-6 left-8 z-10 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3">
              <FaRocket className="text-2xl text-white" />
            </div>

            {/* shadcn Card */}
            <Card className="homepage-card-gradient pt-10 hover:translate-y-[-2px] transition-all duration-300">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  Powered by $REDDICKS
                </h3>

                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-8">
                  $REDDICKS is the fuel. A community-led memecoin on Radix DLT, here to showcase its power, reward our holders, and have a damn good time. Join the revolution.
                </p>

                <a
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="learn-more-btn"
                >
                  Learn More About Reddicks
                </a>
              </CardContent>
            </Card>
          </div>

        </div>
      </section>
    </div>
  );
};

const Homepage = () => {
  return (
    <div className="min-h-screen bg-background">
      <HomepageContent />
    </div>
  );
};

export default Homepage;
